import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { isStaffRoleName } from '../lib/roles';
import { useTheme } from './ThemeContext';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Legacy numeric roles (0/1/2/3) are still consumed by ViewContext, AdminHeader,
// sidebars, FeatureRoute, and several pages. Map current Supabase role names
// back to numeric so existing checks keep working until every consumer migrates
// to useAuth() helpers. Source of truth: public.roles seeded with:
//   platform_admin (1000), org_owner (900), org_admin (800),
//   org_analyst (500), org_viewer (100).
//
// 2026-08-12 — Per-Property User Scoping. `org_scoped` is a real role in the
// backend now (zero permissions; access comes entirely from per-client
// resource_grants). It must map to 3 — the same numeric as org_viewer — so the
// existing role===3 code paths (ViewContext's client-load branch, the sidebar's
// isClient = role===3, FeatureRoute's role===3 redirect) handle scoped users
// identically to org_viewer. Without this, `?? 99` lands and role=99 falls
// through every branch in ViewContext → no clients load → empty dashboard.
// See docs/PER-PROPERTY-USER-SCOPING-EPICS.md (Epic 2).
export const ROLE_NAME_TO_NUMERIC = {
  platform_admin: 0,
  org_owner: 1,
  org_admin: 1,
  org_analyst: 2,
  org_viewer: 3,
  org_scoped: 3,
  // 2026-09-14 — agency-internal staff roles (verified live in the Insider Perks
  // and Modern Campground orgs; see src/lib/roles.js). They are non-client
  // internal staff: map to the legacy "manager" tier (2) so they stay OUT of the
  // role===3 client branches (client-dashboard redirect, client data load) and
  // are NOT treated as admins (0/1). Their real routing rule — land directly in
  // Client Hub, no dashboard — is enforced in LoginPage + the /dashboard route
  // guard via isStaffRoleName, not via this numeric.
  //
  // 2026-10-06 — staff_owner is now a TOGGLE role (see TOGGLE_ROLE_NAMES in
  // src/lib/roles.js): she lands on /dashboard and can switch to Client Hub.
  // Her numeric STAYS 2 — she gets the dashboard data reads ([0,1,2,3] and
  // [0,1,2] gates, mirrored by the BFF ROLE_NAME_TO_NUMERIC) but is not an
  // admin (0/1), so platform-admin-only writes stay gated off.
  //
  // 2026-09-28 — staff_limited added. Same tier (2) for the same reasons as the
  // other staff roles. The "only explicitly granted clients" semantics are
  // enforced by RLS on fn_get_my_clients (the DB is the wall), not by the numeric
  // mapping — so canSelectClients (= !isClient) stays true and the loadClients()
  // branch fires, which calls fn_get_my_clients(); RLS returns only the granted
  // set. isStaffRoleName routes them to /hub/now (Client Hub), not /client-dashboard.
  staff_owner: 2,
  staff_manager: 2,
  staff_member: 2,
  staff_limited: 2,
};

// fn_get_my_profile returns role/org info inside a `memberships` array, not at
// the top level. For a user in multiple orgs we pick the highest-priority
// membership (lower numeric === more privileged in our mapping).
function pickPrimaryMembership(memberships) {
  if (!Array.isArray(memberships) || memberships.length === 0) return null;
  const active = memberships.filter((m) => m?.status === 'active');
  const pool = active.length ? active : memberships;
  return pool.slice().sort((a, b) => {
    const ra = ROLE_NAME_TO_NUMERIC[a?.role_name] ?? 99;
    const rb = ROLE_NAME_TO_NUMERIC[b?.role_name] ?? 99;
    return ra - rb;
  })[0];
}

const LEGACY_LOCALSTORAGE_KEYS = [
  'token', 'username', 'role', 'client_id', 'user_id',
  'avatar', 'theme_preference', 'rememberMe',
];

// GCP-1 fix 2026-07-07 — localStorage.setItem coerces its 2nd arg to string,
// so passing undefined would literally store the text "undefined" (which then
// renders in every "Hi, {username}" surface). Guard all writes.
function safeStore(key, value) {
  if (value === undefined || value === null || value === '' || value === 'undefined' || value === 'null') {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, String(value));
}

function syncLegacyStorage(session, profile) {
  if (session?.access_token) {
    safeStore('token', session.access_token);
  } else {
    localStorage.removeItem('token');
  }
  if (profile) {
    const primary = pickPrimaryMembership(profile.memberships);
    const roleName = primary?.role_name || null;
    // Compute a robust display name: prefer profile.display_name, fall back to
    // first+last, then email, then empty. Never let `undefined` reach setItem.
    const composedName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
    const displayName = profile.display_name || composedName || profile.email || '';
    safeStore('username', displayName);
    safeStore('role', ROLE_NAME_TO_NUMERIC[roleName] ?? 99);
    if (profile.id != null) safeStore('user_id', profile.id);
    // No default_client_id in the new profile RPC. Leave client_id alone here;
    // ViewContext / pages will resolve the active client via fn_get_my_clients.
    if (profile.avatar_url) localStorage.setItem('avatar', profile.avatar_url);
    if (profile.theme_preference) {
      // Legacy mirror — the profile page still reads this key. Epic 3 removes it.
      // The applied theme + localStorage('theme') are owned by ThemeContext now,
      // driven by setTheme() in the profile effect below.
      localStorage.setItem('theme_preference', profile.theme_preference);
    }
  } else {
    ['username', 'role', 'client_id', 'user_id', 'avatar', 'theme_preference'].forEach(k => localStorage.removeItem(k));
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Epic 1 (item 35 Q2/Q6) — USER-level, session-stable flag: true for
  // platform_admin and the six full-access staff, false for everyone else.
  // Fetched once per session in fetchProfile alongside fn_get_my_profile.
  const [canOpenClientDashboard, setCanOpenClientDashboard] = useState(false);
  const fetchedProfileForUserId = useRef(null);
  // Team member ID — ops_team_members.id. This is a DIFFERENT UUID from the
  // Supabase auth user ID (auth.users.id): ops_team_members.auth_user_id is
  // the FK back to auth.users.id, while ops_team_members.id is what task
  // owner_id references. Owner-scoped RPCs (e.g. the supplementary done-bucket
  // fn_work_list p_owner_id) need this ID, not the auth user ID. Fetched once
  // per session; null when the user has no row in ops_team_members yet (a
  // client viewer won't have one, and that's fine — those users never own
  // work items). See work-page-fixes.md §A Item 1.
  const [teamMemberId, setTeamMemberId] = useState(null);

  // ThemeContext is the single source of truth for the APPLIED theme. When the
  // profile loads (fn_get_my_profile already fetches theme_preference at line
  // 120), push the server value into ThemeContext via setTheme(). This reuses
  // the existing fetch — no duplicate round-trip — and replaces the old direct
  // applyTheme()+localStorage('theme') writes that bypassed ThemeContext's
  // state (the "saved but not read back on reload" split-brain). On sign-out
  // (profile → null) we leave the cached theme so the login page keeps it.
  const { setTheme } = useTheme();
  useEffect(() => {
    if (profile?.theme_preference) setTheme(profile.theme_preference);
  }, [profile, setTheme]);

  const fetchProfile = useCallback(async (currentSession) => {
    if (!currentSession) {
      setProfile(null);
      setCanOpenClientDashboard(false);
      setTeamMemberId(null);
      syncLegacyStorage(null, null);
      return null;
    }
    const { data, error } = await supabase.rpc('fn_get_my_profile');
    if (error) {
      console.error('fn_get_my_profile error:', error.message);
      setProfile(null);
      setCanOpenClientDashboard(false);
      setTeamMemberId(null);
      syncLegacyStorage(currentSession, null);
      return null;
    }
    const p = Array.isArray(data) ? data[0] : data;
    setProfile(p || null);
    syncLegacyStorage(currentSession, p);
    fetchedProfileForUserId.current = currentSession.user?.id || null;

    // Team member ID lookup — ops_team_members.id where auth_user_id matches
    // the session's auth user ID. .maybeSingle() returns null (not an error)
    // when no row exists, which is the expected case for client viewers and
    // anyone not yet in the staff table. On any error we default to null so
    // owner-scoped RPCs simply drop the p_owner_id param (safe degradation)
    // rather than sending the wrong ID space. See work-page-fixes.md §A Item 1.
    try {
      const authUserId = currentSession.user?.id;
      if (authUserId) {
        const { data: memberRow, error: memberErr } = await supabase
          .from('ops_team_members')
          .select('id')
          .eq('auth_user_id', authUserId)
          .maybeSingle();
        if (memberErr) {
          console.error('ops_team_members lookup error:', memberErr.message);
          setTeamMemberId(null);
        } else {
          setTeamMemberId(memberRow?.id || null);
        }
      } else {
        setTeamMemberId(null);
      }
    } catch (e) {
      setTeamMemberId(null);
    }

    // Epic 1 (item 35 Q2/Q6) — fn_can_open_client_dashboard() is the single
    // USER-level signal that the six full-access staff get the client
    // dashboard. It is session-scoped (no args; runs as auth.uid()), fetched
    // once per session alongside the profile. Coerced with Boolean(data) and
    // DEFAULTS TO FALSE on any error — never true. A defaulted true would
    // silently grant the six dashboard access on a transient failure, which
    // is the opposite of safe. Bare boolean per Q6 (verified shape); if the
    // RPC ever returns an envelope, Boolean() still reduces it correctly.
    try {
      const { data: canOpen, error: canOpenErr } = await supabase.rpc('fn_can_open_client_dashboard');
      setCanOpenClientDashboard(!canOpenErr && Boolean(canOpen));
    } catch {
      setCanOpenClientDashboard(false);
    }

    return p;
  }, []);

  // Mirror profile into a ref so the onAuthStateChange closure can read the
  // current value without re-subscribing every time the profile updates.
  const profileRef = useRef(null);
  profileRef.current = profile;

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const initialSession = data.session;
      setSession(initialSession);
      if (initialSession) {
        fetchProfile(initialSession).finally(() => mounted && setLoading(false));
      } else {
        syncLegacyStorage(null, null);
        setLoading(false);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      if (event === 'SIGNED_OUT' || !newSession) {
        setProfile(null);
        setCanOpenClientDashboard(false);
        fetchedProfileForUserId.current = null;
        LEGACY_LOCALSTORAGE_KEYS.forEach(k => localStorage.removeItem(k));
        return;
      }
      const userId = newSession.user?.id;
      if (userId && fetchedProfileForUserId.current !== userId) {
        fetchProfile(newSession);
      } else {
        syncLegacyStorage(newSession, profileRef.current);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const resetPassword = useCallback(async (email) => {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }, []);

  const updateProfile = useCallback(async ({ display_name, avatar_url, theme_preference } = {}) => {
    const { data, error } = await supabase.rpc('fn_update_my_profile', {
      p_display_name: display_name ?? null,
      p_avatar_url: avatar_url ?? null,
      p_theme_preference: theme_preference ?? null,
    });
    if (error) throw error;
    const updated = Array.isArray(data) ? data[0] : data;
    if (updated) {
      setProfile(updated);
      syncLegacyStorage(session, updated);
    }
    return updated;
  }, [session]);

  const refreshProfile = useCallback(async () => fetchProfile(session), [fetchProfile, session]);

  const primaryMembership = pickPrimaryMembership(profile?.memberships);
  const roleName = primaryMembership?.role_name || null;
  const orgId = primaryMembership?.org_id || null;
  const memberships = profile?.memberships || [];

  const isPlatformAdmin = roleName === 'platform_admin';
  const isOrgOwner = roleName === 'org_owner';
  const isOrgAdmin = roleName === 'org_admin';
  const isAnalyst = roleName === 'org_analyst';
  // 2026-08-12 — `org_scoped` is treated like org_viewer for shell/nav purposes
  // (Epic 2): it is a low-privilege membership whose real access comes from
  // per-client resource_grants, resolved via fn_get_my_clients. ViewContext and
  // the sidebars branch on the numeric role (=== 3), not on isClient, but
  // isClient is exposed and consumed by some pages, so keep it consistent.
  const isClient = roleName === 'org_viewer' || roleName === 'org_scoped';
    // 2026-09-14 — agency-internal staff. staff_owner (2026-10-06) and staff_manager
  // (2026-10-07) were promoted to TOGGLE roles (land on /dashboard, can switch to
  // Client Hub); the remaining staff (staff_member / staff_limited) are
  // Client-Hub-only: redirected to /hub/now on login and bounced from /dashboard.
  // See src/lib/roles.js for the routing rule.
  const isStaff = isStaffRoleName(roleName);

  // Aliases preserved for legacy code that imports `isSuperAdmin` / `isManager`
  // by name. platform_admin replaces super_admin; "manager" is the legacy
  // numeric-2 role, which now maps to org_analyst.
  const isSuperAdmin = isPlatformAdmin;
  const isManager = isAnalyst;
  const isAdmin = isPlatformAdmin || isOrgOwner || isOrgAdmin;
  const canSelectClients = roleName != null && !isClient;

  const value = {
    session,
    user: session?.user || null,
    profile,
    // ops_team_members.id for the current user (null if not in the table).
    // Distinct from the auth user ID — see work-page-fixes.md §A Item 1.
    teamMemberId,
    memberships,
    primaryMembership,
    orgId,
    loading,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    refreshProfile,
    roleName,
    isPlatformAdmin,
    isOrgOwner,
    isOrgAdmin,
    isAnalyst,
    isClient,
    isStaff,
    isSuperAdmin,
    isManager,
    isAdmin,
    canSelectClients,
    canOpenClientDashboard,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
